// Silpoice frontend (docs/SPEC.md §8): login/logout, account + cart summary
// (loaded immediately after login via /api/account, not gated on connecting
// to voice), connect button, mic toggle, connection status, and a live
// transcript. Cart sync while talking is done by polling /api/account —
// livekit_worker.py doesn't publish structured turn data over the data
// channel yet, so there's no push signal to react to; polling is the
// pragmatic stand-in until that exists.

import { Room, RoomEvent, Track, createLocalAudioTrack, type LocalAudioTrack } from "livekit-client";

const loginCard = document.getElementById("login-card") as HTMLElement;
const accountCard = document.getElementById("account-card") as HTMLElement;
const cartCard = document.getElementById("cart-card") as HTMLElement;
const voiceCard = document.getElementById("voice-card") as HTMLElement;

const loginButton = document.getElementById("login-button") as HTMLButtonElement;
const logoutButton = document.getElementById("logout-button") as HTMLButtonElement;
const connectButton = document.getElementById("connect-button") as HTMLButtonElement;
const micButton = document.getElementById("mic-button") as HTMLButtonElement;
const restartButton = document.getElementById("restart-button") as HTMLButtonElement;
const endButton = document.getElementById("end-button") as HTMLButtonElement;
const agentWaveEl = document.getElementById("agent-wave") as HTMLElement;
const statusEl = document.getElementById("status") as HTMLElement;
const transcriptEl = document.getElementById("transcript") as HTMLElement;
const audioContainer = document.getElementById("audio-container") as HTMLElement;

const avatarInitialEl = document.getElementById("avatar-initial") as HTMLElement;
const accountNameEl = document.getElementById("account-name") as HTMLElement;
const accountPhoneEl = document.getElementById("account-phone") as HTMLElement;
const cartSummaryEl = document.getElementById("cart-summary") as HTMLElement;
const cartItemsEl = document.getElementById("cart-items") as HTMLElement;
const cartTotalEl = document.getElementById("cart-total") as HTMLElement;
const cartDeliveryEl = document.getElementById("cart-delivery") as HTMLElement;
const deliveryTypeEl = document.getElementById("delivery-type") as HTMLElement;
const deliveryAddressEl = document.getElementById("delivery-address") as HTMLElement;
const deliveryTimeslotEl = document.getElementById("delivery-timeslot") as HTMLElement;
const checkoutLinkEl = document.getElementById("checkout-link") as HTMLAnchorElement;

let room: Room | null = null;
let cartPollTimer: ReturnType<typeof setInterval> | null = null;

const CART_POLL_INTERVAL_MS = 4000;

interface AccountProfile {
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  email: string | null;
}

interface AccountCartItem {
  name: string;
  quantity: number;
  ratio: string;
  total: number;
  image: string | null;
}

interface AccountDelivery {
  type: string;
  address: string | null;
  timeslotStart: string | null;
  timeslotEnd: string | null;
}

interface AccountCart {
  exists: boolean;
  itemCount: number;
  total: number;
  deliveryFee: number;
  items: AccountCartItem[];
  delivery: AccountDelivery | null;
}

const DELIVERY_TYPE_LABELS: Record<string, string> = {
  SelfPickup: "Самовивіз",
  DeliveryHome: "Доставка додому",
  DeliveryExpressByPromise: "Експрес-доставка",
  LongDelivery: "Доставка",
  WideAssortDelivery: "Доставка (широкий асортимент)",
  NovaPoshta: "Нова пошта",
  B2B: "B2B доставка",
  PreOrder: "Передзамовлення",
};

// Explicit timeZone: Silpo only delivers within Ukraine, so delivery
// timeslots are always Kyiv time regardless of the viewer's own device
// timezone — without this, a browser outside Ukraine (e.g. during a demo
// recorded abroad) would silently convert to its own local time, which
// wouldn't match the delivery window Silpoice speaks aloud (see
// assistant.py's KYIV_TZ grounding) or Silpo's own actual delivery time.
const KYIV_TZ = "Europe/Kyiv";
const timeslotFormatter = new Intl.DateTimeFormat("uk-UA", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: KYIV_TZ,
});
const timeFormatter = new Intl.DateTimeFormat("uk-UA", {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: KYIV_TZ,
});

function setStatus(text: string): void {
  statusEl.textContent = text;
}

function appendTranscript(speaker: "Ви" | "Silpoice", text: string): void {
  const line = document.createElement("p");
  const tag = document.createElement("span");
  tag.className = `speaker-tag speaker-${speaker === "Ви" ? "user" : "agent"}`;
  tag.textContent = speaker;
  line.appendChild(tag);
  line.appendChild(document.createTextNode(text));
  transcriptEl.appendChild(line);
  transcriptEl.scrollTop = transcriptEl.scrollHeight;
}

function pluralizeTovar(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "товар";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "товари";
  return "товарів";
}

// --- Signals: what the agent is working from --------------------------------
// agent/signals.py publishes these on the room's data channel: one household
// snapshot per session, then one message per tool call. Display wording lives
// here, not in the agent.

const SIGNALS_TOPIC = "silpoice.signals";
const MAX_ACTIVITY_ITEMS = 8;

const signalsCard = document.getElementById("signals-card") as HTMLElement;
const predictionsEl = document.getElementById("signals-predictions") as HTMLElement;
const eventsEl = document.getElementById("signals-events") as HTMLElement;
const stockEl = document.getElementById("signals-stock") as HTMLElement;
const activityBlock = document.getElementById("agent-activity-block") as HTMLElement;
const activityEl = document.getElementById("agent-activity") as HTMLElement;

interface Prediction {
  item_id: string;
  name_uk: string;
  unit: string;
  current_qty: number;
  days_to_depletion: number;
  suggested_qty: number;
  triggering_event: string | null;
}

interface HouseholdEvent {
  date: string;
  name_uk: string;
  category_multipliers: Record<string, number>;
}

interface StockLevel {
  item_id: string;
  name_uk: string;
  unit: string;
  current_qty: number;
  par_level: number;
}

type SignalMessage =
  | { type: "snapshot"; predictions: Prediction[]; events: HouseholdEvent[]; stock: StockLevel[] }
  | { type: "tool"; name: string; ok: boolean }
  | { type: "auth_expired" };

const CATEGORY_LABELS: Record<string, string> = {
  dairy: "молочне",
  bakery: "випічка",
  meat: "м'ясо",
  produce: "овочі",
  pantry: "бакалія",
  beverages: "напої",
  sweets: "солодощі",
  household: "побутове",
};

const TOOL_LABELS: Record<string, string> = {
  predict_shopping_list: "Прогноз: що закінчується",
  get_upcoming_events: "Календар подій",
  get_stock_levels: "Запаси вдома",
  get_consumption_history: "Історія споживання",
  silpo_find_products_batch: "Пошук товарів",
  silpo_get_products: "Пошук товарів",
  silpo_get_product_details: "Деталі товару",
  silpo_get_similar_products: "Схожі товари",
  silpo_get_replacements: "Заміна відсутнього товару",
  silpo_get_my_shopping_cart: "Кошик гостя",
  silpo_get_shopping_cart_by_id: "Перевірка кошика",
  silpo_create_shopping_cart: "Створення кошика",
  silpo_add_or_update_cart_products: "Додавання в кошик",
  silpo_remove_cart_products: "Видалення з кошика",
  silpo_clear_shopping_cart: "Очищення кошика",
  silpo_update_shopping_cart: "Налаштування доставки",
  silpo_get_time_slots: "Вільні слоти доставки",
  silpo_find_address: "Пошук адреси",
  silpo_get_available_delivery_types: "Способи доставки",
  silpo_list_branches: "Магазини поруч",
  silpo_get_my_family: "Сім'я гостя",
  silpo_get_my_food_restrictions: "Харчові обмеження",
  silpo_get_my_online_orders: "Історія замовлень",
  end_conversation: "Завершення розмови",
};

const dayMonthFormatter = new Intl.DateTimeFormat("uk-UA", { day: "numeric", month: "long" });
const qtyFormatter = new Intl.NumberFormat("uk-UA", { maximumFractionDigits: 1 });

function pluralUk(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

function formatQty(qty: number, unit: string): string {
  const value = unit === "шт" ? Math.ceil(qty) : qty;
  return `${qtyFormatter.format(value)} ${unit}`;
}

function formatDaysLeft(days: number): string {
  if (days < 1) return "менше ніж на добу";
  const n = Math.round(days);
  return `на ~${n} ${pluralUk(n, "день", "дні", "днів")}`;
}

function formatEventWhen(isoDate: string): string {
  const eventDay = new Date(`${isoDate}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const inDays = Math.round((eventDay.getTime() - today.getTime()) / 86_400_000);
  const when = inDays <= 0 ? "сьогодні" : inDays === 1 ? "завтра" : `через ${inDays} ${pluralUk(inDays, "день", "дні", "днів")}`;
  return `${dayMonthFormatter.format(eventDay)}, ${when}`;
}

function listItem(main: string, sub: string | null, when: string, urgent = false): HTMLLIElement {
  const li = document.createElement("li");
  const left = document.createElement("div");
  const mainEl = document.createElement("span");
  mainEl.className = "signal-main";
  mainEl.textContent = main;
  left.appendChild(mainEl);
  if (sub) {
    const subEl = document.createElement("span");
    subEl.className = "signal-sub";
    subEl.textContent = sub;
    left.appendChild(subEl);
  }
  const whenEl = document.createElement("span");
  whenEl.className = `signal-when${urgent ? " urgent" : ""}`;
  whenEl.textContent = when;
  li.append(left, whenEl);
  return li;
}

function renderSnapshot(msg: Extract<SignalMessage, { type: "snapshot" }>): void {
  predictionsEl.replaceChildren();
  if (msg.predictions.length === 0) {
    predictionsEl.appendChild(listItem("Нічого не закінчується найближчими днями", null, ""));
  }
  for (const p of msg.predictions) {
    const li = listItem(
      p.name_uk,
      `Лишилось ${formatQty(p.current_qty, p.unit)} · докупити ${formatQty(p.suggested_qty, p.unit)}`,
      formatDaysLeft(p.days_to_depletion),
      p.days_to_depletion < 2,
    );
    if (p.triggering_event) {
      const tag = document.createElement("span");
      tag.className = "signal-event-tag";
      tag.textContent = `через подію: ${p.triggering_event}`;
      li.firstElementChild?.appendChild(tag);
    }
    predictionsEl.appendChild(li);
  }

  eventsEl.replaceChildren();
  if (msg.events.length === 0) {
    eventsEl.appendChild(listItem("Подій найближчим часом немає", null, ""));
  }
  for (const e of msg.events) {
    const boosts = Object.entries(e.category_multipliers)
      .map(([cat, mult]) => `${CATEGORY_LABELS[cat] ?? cat} ×${qtyFormatter.format(mult)}`)
      .join(", ");
    eventsEl.appendChild(listItem(e.name_uk, boosts ? `Більше споживання: ${boosts}` : null, formatEventWhen(e.date)));
  }

  const lowIds = new Set(msg.predictions.map((p) => p.item_id));
  const byFill = [...msg.stock].sort((a, b) => a.current_qty / a.par_level - b.current_qty / b.par_level);
  stockEl.replaceChildren();
  for (const s of byFill) {
    const li = document.createElement("li");
    if (lowIds.has(s.item_id)) li.className = "low";
    const name = document.createElement("span");
    name.textContent = s.name_uk;
    const track = document.createElement("span");
    track.className = "stock-track";
    const fill = document.createElement("span");
    fill.className = "stock-fill";
    fill.style.display = "block";
    fill.style.width = `${Math.min(100, Math.round((s.current_qty / s.par_level) * 100))}%`;
    track.appendChild(fill);
    const qty = document.createElement("span");
    qty.className = "stock-qty";
    qty.textContent = `${formatQty(s.current_qty, s.unit)} / ${formatQty(s.par_level, s.unit)}`;
    li.append(name, track, qty);
    stockEl.appendChild(li);
  }

  signalsCard.hidden = false;
}

function addActivity(name: string, ok: boolean): void {
  const li = document.createElement("li");
  if (!ok) li.className = "failed";
  const source = document.createElement("span");
  const isSilpo = name.startsWith("silpo_");
  source.className = `activity-source${isSilpo ? " silpo" : ""}`;
  source.textContent = isSilpo ? "Сільпо MCP" : name === "end_conversation" ? "Розмова" : "Дім";
  const label = document.createElement("span");
  label.textContent = TOOL_LABELS[name] ?? name;
  li.append(source, label);
  activityEl.prepend(li);
  while (activityEl.children.length > MAX_ACTIVITY_ITEMS) activityEl.lastElementChild?.remove();
  activityBlock.hidden = false;
}

function handleSignal(payload: Uint8Array): void {
  let msg: SignalMessage;
  try {
    msg = JSON.parse(new TextDecoder().decode(payload)) as SignalMessage;
  } catch {
    return;
  }
  if (msg.type === "snapshot") renderSnapshot(msg);
  else if (msg.type === "tool") addActivity(msg.name, msg.ok);
  // The agent found Silpo rejecting the token: it says so and hangs up, and
  // the Disconnected handler then shows the sign-in card.
  else if (msg.type === "auth_expired") authExpiredDuringCall = true;
}

function renderAccount(profile: AccountProfile | null): void {
  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") || "Гість Сільпо";
  accountNameEl.textContent = fullName;
  accountPhoneEl.textContent = profile?.phone ?? profile?.email ?? "";
  avatarInitialEl.textContent = (profile?.firstName?.[0] ?? "С").toUpperCase();
}

function renderDelivery(delivery: AccountDelivery | null): void {
  if (!delivery) {
    cartDeliveryEl.hidden = true;
    return;
  }

  deliveryTypeEl.textContent = `🏬 ${DELIVERY_TYPE_LABELS[delivery.type] ?? delivery.type}`;
  deliveryAddressEl.textContent = delivery.address ? `📍 ${delivery.address}` : "";
  deliveryAddressEl.hidden = !delivery.address;

  if (delivery.timeslotStart && delivery.timeslotEnd) {
    const start = new Date(delivery.timeslotStart);
    const end = new Date(delivery.timeslotEnd);
    deliveryTimeslotEl.textContent = `🕐 ${timeslotFormatter.format(start)}–${timeFormatter.format(end)}`;
    deliveryTimeslotEl.hidden = false;
  } else {
    deliveryTimeslotEl.hidden = true;
  }

  cartDeliveryEl.hidden = false;
}

function renderCart(cart: AccountCart): void {
  cartItemsEl.innerHTML = "";

  renderDelivery(cart.delivery);

  if (!cart.exists) {
    cartSummaryEl.textContent = "Кошик порожній.";
    cartSummaryEl.hidden = false;
    cartTotalEl.hidden = true;
    checkoutLinkEl.hidden = true;
    return;
  }

  checkoutLinkEl.hidden = false;

  cartSummaryEl.textContent = `${cart.itemCount} ${pluralizeTovar(cart.itemCount)}:`;
  cartSummaryEl.hidden = false;

  for (const item of cart.items) {
    const li = document.createElement("li");

    if (item.image) {
      const imgEl = document.createElement("img");
      imgEl.className = "cart-item-image";
      imgEl.src = item.image;
      imgEl.alt = "";
      imgEl.loading = "lazy";
      li.appendChild(imgEl);
    }

    const nameEl = document.createElement("span");
    nameEl.className = "cart-item-name";
    nameEl.textContent = `${item.name} · ${item.quantity} ${item.ratio}`;
    const priceEl = document.createElement("span");
    priceEl.className = "cart-item-price";
    priceEl.textContent = `${item.total.toFixed(2)} грн`;
    li.appendChild(nameEl);
    li.appendChild(priceEl);
    cartItemsEl.appendChild(li);
  }

  if (cart.deliveryFee > 0) {
    const li = document.createElement("li");
    li.className = "cart-item-delivery";
    const nameEl = document.createElement("span");
    nameEl.className = "cart-item-name";
    nameEl.textContent = "🚚 Доставка";
    const priceEl = document.createElement("span");
    priceEl.className = "cart-item-price";
    priceEl.textContent = `${cart.deliveryFee.toFixed(2)} грн`;
    li.appendChild(nameEl);
    li.appendChild(priceEl);
    cartItemsEl.appendChild(li);
  }

  cartTotalEl.textContent = `Разом: ${cart.total.toFixed(2)} грн`;
  cartTotalEl.hidden = false;
}

// The Worker answers 401 when Silpo rejected the stored token (it has already
// forgotten it), e.g. after the Guest signed in on another device. Back to the
// sign-in card with a note, instead of a cart that silently fails to load.
const reauthNote = document.getElementById("reauth-note") as HTMLElement;
let authExpiredDuringCall = false;

function showReauth(): void {
  stopCartPolling();
  // End the call too, not just hide it: hiding the voice card while the room
  // stayed connected left the agent talking with no button to stop it. The
  // agent closes its session when the guest leaves the room.
  if (room) {
    const closing = room;
    room = null;
    void closing.disconnect();
  }
  loginCard.hidden = false;
  reauthNote.hidden = false;
  logoutButton.hidden = true;
  accountCard.hidden = true;
  cartCard.hidden = true;
  voiceCard.hidden = true;
  signalsCard.hidden = true;
}

async function loadAccount(): Promise<void> {
  accountCard.hidden = false;
  cartCard.hidden = false;
  try {
    const resp = await fetch("/api/account");
    if (resp.status === 401) {
      showReauth();
      return;
    }
    if (!resp.ok) {
      cartSummaryEl.textContent = "Не вдалося завантажити кошик.";
      return;
    }
    const { profile, cart } = (await resp.json()) as { profile: AccountProfile | null; cart: AccountCart };
    renderAccount(profile);
    renderCart(cart);
  } catch (err) {
    console.error(err);
    cartSummaryEl.textContent = "Не вдалося завантажити кошик.";
  }
}

async function refreshLoginState(): Promise<void> {
  const resp = await fetch("/api/session");
  const { loggedIn } = (await resp.json()) as { loggedIn: boolean };

  loginCard.hidden = loggedIn;
  logoutButton.hidden = !loggedIn;
  voiceCard.hidden = !loggedIn;

  if (loggedIn) {
    await loadAccount();
  } else {
    accountCard.hidden = true;
    cartCard.hidden = true;
  }
}

loginButton.addEventListener("click", () => {
  window.location.href = "/auth/login";
});

function stopCartPolling(): void {
  if (cartPollTimer !== null) {
    clearInterval(cartPollTimer);
    cartPollTimer = null;
  }
}

function startCartPolling(): void {
  stopCartPolling();
  cartPollTimer = setInterval(loadAccount, CART_POLL_INTERVAL_MS);
}

logoutButton.addEventListener("click", async () => {
  stopCartPolling();
  room?.disconnect();
  room = null;
  await fetch("/api/logout", { method: "POST" });
  window.location.href = "/";
});

const AGENT_WAVE_LABELS: Record<string, string> = {
  listening: "Слухає",
  thinking: "Думає",
  speaking: "Говорить",
};

function setAgentWaveState(state: string): void {
  agentWaveEl.dataset.state = state;
  agentWaveEl.hidden = false;
  const label = AGENT_WAVE_LABELS[state];
  if (label) setStatus(label);
}

function resetVoiceUI(): void {
  stopCartPolling();
  connectButton.hidden = false;
  connectButton.disabled = false;
  micButton.hidden = true;
  restartButton.hidden = true;
  endButton.hidden = true;
  hideAudioUnlock();
  agentWaveEl.hidden = true;
  audioContainer.innerHTML = "";
  transcriptEl.innerHTML = "";
  // The signals card stays up after a call so it can still be read; only the
  // live activity list belongs to the call that just ended.
  activityEl.replaceChildren();
  activityBlock.hidden = true;
  setStatus("Не з'єднано.");
}

// Browsers (Safari always, Chrome sometimes) refuse to play the agent's audio
// unless playback was unlocked by a click. The agent then talks and the guest
// hears nothing. This button is the manual unlock when the automatic one fails.
let audioUnlockButton: HTMLButtonElement | null = null;

function hideAudioUnlock(): void {
  audioUnlockButton?.remove();
  audioUnlockButton = null;
}

function showAudioUnlock(r: Room): void {
  if (audioUnlockButton) return;
  audioUnlockButton = document.createElement("button");
  audioUnlockButton.type = "button";
  audioUnlockButton.textContent = "🔊 Увімкнути звук Silpoice";
  audioUnlockButton.addEventListener("click", () => {
    void r.startAudio().then(() => {
      if (r.canPlaybackAudio) hideAudioUnlock();
    });
  });
  statusEl.after(audioUnlockButton);
}

async function connectToRoom(): Promise<void> {
  connectButton.disabled = true;
  authExpiredDuringCall = false;

  // Created and unlocked synchronously, while the click still counts as a user
  // gesture — after the mic prompt and the token fetch it no longer would.
  const newRoom = new Room();
  void newRoom.startAudio();

  // Ask for the microphone BEFORE joining the room. The agent is dispatched
  // the moment the browser joins, and used to start its greeting while the
  // permission prompt was still on screen — the guest then heard it only
  // from the middle once the mic (and the phone's audio route) came up.
  let micTrack: LocalAudioTrack;
  try {
    setStatus("Дозвольте доступ до мікрофона…");
    micTrack = await createLocalAudioTrack({ echoCancellation: true, noiseSuppression: true });
  } catch (err) {
    console.error(err);
    setStatus("Без мікрофона Silpoice вас не почує. Дозвольте доступ до мікрофона в налаштуваннях браузера й спробуйте ще раз.");
    connectButton.disabled = false;
    return;
  }

  setStatus("З'єднання...");
  try {
    const resp = await fetch("/api/livekit-token", { method: "POST" });
    if (resp.status === 401) {
      micTrack.stop();
      resetVoiceUI();
      showReauth();
      return;
    }
    if (!resp.ok) throw new Error(`livekit-token: HTTP ${resp.status}`);
    const { token, url } = (await resp.json()) as { token: string; url: string; roomName: string };

    room = newRoom;
    room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      if (newRoom.canPlaybackAudio) hideAudioUnlock();
      else showAudioUnlock(newRoom);
    });

    // Joining the room is not the same as the agent being there: on LiveKit
    // Cloud's free plan an idle agent cold-starts, which takes 10-20s. Say so
    // until the agent actually arrives, instead of "speak now" into silence.
    let agentJoined = false;
    const markAgentJoined = (): void => {
      if (agentJoined) return;
      agentJoined = true;
      setStatus("З'єднано. Говоріть — Silpoice слухає.");
    };
    room.on(RoomEvent.Connected, () => {
      setStatus("Підключаю асистента…");
      startCartPolling();
    });
    room.on(RoomEvent.Disconnected, () => {
      resetVoiceUI();
      if (authExpiredDuringCall) showReauth();
    });

    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind === Track.Kind.Audio) {
        const el = track.attach();
        audioContainer.appendChild(el);
      }
    });

    // Agent liveness indicator: livekit-agents publishes the agent's
    // listening/thinking/speaking state as the "lk.agent.state" participant
    // attribute on its own participant (not the local one) — a visible wave
    // driven by this is the difference between "still working" and "hung"
    // during the multi-second gaps a tool call or TTS retry can take.
    room.on(RoomEvent.ParticipantAttributesChanged, (changed, participant) => {
      if (participant.isLocal) return;
      const state = changed["lk.agent.state"];
      if (state) setAgentWaveState(state);
    });
    room.on(RoomEvent.ParticipantConnected, (participant) => {
      markAgentJoined();
      const state = participant.attributes["lk.agent.state"];
      if (state) setAgentWaveState(state);
    });

    room.on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
      if (topic === SIGNALS_TOPIC) handleSignal(payload);
    });

    // Beta LiveKit API — payload beyond segments[].text isn't stabilized,
    // so this stays defensive rather than asserting a strict type.
    room.on(
      RoomEvent.TranscriptionReceived,
      (segments: Array<{ text: string; final?: boolean }>, participant?: { isLocal: boolean }) => {
        const speaker = participant?.isLocal ? "Ви" : "Silpoice";
        for (const segment of segments) {
          if (segment.final) appendTranscript(speaker, segment.text);
        }
      },
    );

    await room.connect(url, token);
    await room.localParticipant.publishTrack(micTrack);
    if (room.remoteParticipants.size > 0) markAgentJoined();
    if (!room.canPlaybackAudio) showAudioUnlock(room);
    micButton.hidden = false;
    micButton.textContent = "🎤 Мікрофон увімкнено";
    restartButton.hidden = false;
    endButton.hidden = false;
    connectButton.hidden = true;
  } catch (err) {
    console.error(err);
    setStatus(`Помилка з'єднання: ${(err as Error).message}`);
    micTrack.stop();
    connectButton.disabled = false;
  }
}

connectButton.addEventListener("click", () => {
  void connectToRoom();
});

micButton.addEventListener("click", async () => {
  if (!room) return;
  const enabled = room.localParticipant.isMicrophoneEnabled;
  await room.localParticipant.setMicrophoneEnabled(!enabled);
  micButton.textContent = enabled ? "🔇 Мікрофон вимкнено" : "🎤 Мікрофон увімкнено";
});

endButton.addEventListener("click", async () => {
  // The Disconnected handler above already calls resetVoiceUI() once
  // room.disconnect() completes — no need to duplicate it here.
  if (room) {
    await room.disconnect();
    room = null;
  } else {
    resetVoiceUI();
  }
});

restartButton.addEventListener("click", async () => {
  if (room) {
    await room.disconnect();
    room = null;
  }
  await connectToRoom();
});

refreshLoginState();
